import type { FC } from 'react';

import type { MeasureVm } from '@eluve/api-contract';
import {
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';

export const TrendSparkline: FC<{ trend: MeasureVm['trend'] }> = ({
  trend,
}) => {
  const { points } = trend;
  if (points.length < 2) return null;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const lastValue = points.at(-1)?.value ?? min;
  const W = 72;
  const H = 20;
  const step = points.length > 1 ? W / (points.length - 1) : 0;
  const coords = points.map((p, i) => {
    const x = i * step;
    const y = H - ((p.value - min) / span) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          className="overflow-visible text-text-insights"
          role="img"
          aria-label="Measure trend"
          tabIndex={0}
        >
          <polyline
            points={coords.join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <circle
            cx={(points.length - 1) * step}
            cy={H - ((lastValue - min) / span) * H}
            r={2.5}
            fill="currentColor"
          />
        </svg>
      </TooltipTrigger>
      <TooltipContent>
        <Stack direction="vertical" gap={0.5}>
          {points.map((p, i) => (
            <span key={`${p.date}-${i}`}>
              {p.date}: {p.value}
              {p.unit ? ` ${p.unit}` : ''}
            </span>
          ))}
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};
